'use client';

import { FC, useEffect, useRef, useState } from 'react';

import { useGetAccountPhoto, useUploadAccountPhoto } from '@/data';

import { UPLOAD_ERROR_MESSAGE } from './constants';
import { PictureUploader } from '../PictureUploader';

const AccountPhoto: FC<IAccountPhotoProps> = ({
  value,
  loading,
  onChange,
  title,
  alt,
  field,
  rounded,
}) => {
  const [isLoading, setIsLoading] = useState(loading);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploadSequence, setIsUploadSequence] = useState(false);
  const photo = useRef<File | null>(null);

  const { data, loading: isLoadingPhoto } = useGetAccountPhoto({
    skip: !value,
    variables: {
      filename: value,
    },
  });

  useEffect(() => {
    if (isUploadSequence && uploadProgress === 100 && isLoadingPhoto) {
      setUploadProgress(95);
    }
  }, [isUploadSequence, uploadProgress, isLoadingPhoto]);

  useEffect(() => {
    setIsLoading(loading);
  }, [loading]);

  useEffect(() => {
    if (isUploadSequence && !isLoadingPhoto && uploadProgress >= 95) {
      setUploadProgress(100);

      setTimeout(() => {
        setIsUploadSequence(false);
        setUploadProgress(0);
        setIsLoading(false);
      }, 1000);
    }
  }, [isUploadSequence, isLoadingPhoto, uploadProgress]);

  const { uploadAccountPhoto } = useUploadAccountPhoto({
    onCompleted: res => {
      import('axios').then(mod => {
        const axios = mod.default;
        const { file_name, url, headers } = res.uploadAccountPhoto ?? {};

        axios
          .put(url ?? '', photo?.current, {
            headers: {
              'Content-Type': (photo?.current as any)?.type,
              ...(headers?.reduce(
                (prev, header) => ({
                  ...prev,
                  [header?.key ?? '']: header?.value,
                }),
                {},
              ) ?? {}),
            },
            onUploadProgress: progressEvent => {
              const percentCompleted = progressEvent.total
                ? 10 +
                  Math.round((progressEvent.loaded * 80) / progressEvent.total)
                : 10;
              setUploadProgress(percentCompleted);
            },
          })
          .then(uploadResponse => {
            if (uploadResponse.status !== 200) {
              onChange(field, null, UPLOAD_ERROR_MESSAGE);
              setIsUploadSequence(false);
              setIsLoading(false);
              return;
            }

            setUploadProgress(95);
            onChange(field, file_name, null);
          })
          .catch(e => {
            // eslint-disable-next-line no-console
            console.error(e);
            onChange(field, null, UPLOAD_ERROR_MESSAGE);
            setIsUploadSequence(false);
            setIsLoading(false);
          });
      });
    },
    onError: e => {
      // eslint-disable-next-line no-console
      console.error(e);
      onChange(field, null, UPLOAD_ERROR_MESSAGE);
      setIsUploadSequence(false);
      setIsLoading(false);
    },
  });

  const handleChange = ({ file, error }: { file?: File; error?: string }) => {
    if (error) {
      onChange(field, null, error);
      return;
    }

    if (file) {
      setIsLoading(true);
      setIsUploadSequence(true);
      setUploadProgress(10);
      photo.current = file;

      uploadAccountPhoto({
        variables: {
          input: {
            file_name: file.name,
            file_type: file.type,
          },
        },
      });
    }
  };

  return (
    <PictureUploader
      alt={alt}
      imageSrc={data}
      isLoading={isLoading || isLoadingPhoto}
      isUploadSequence={isUploadSequence}
      onChange={handleChange}
      progress={uploadProgress}
      rounded={rounded}
      title={title}
    />
  );
};

interface IAccountPhotoProps {
  alt?: string;
  field: string;
  loading?: boolean;
  // eslint-disable-next-line no-unused-vars
  onChange: (field: any, url: any, error: string | null) => void;
  rounded?: boolean;
  title: string;
  value: any;
}

export default AccountPhoto;
